import * as React from "react";
import { Layout } from "../../../private/components/layout";
import { logger } from "../../..";
import { SelectStep } from "./select-step";
import { FormStepData, initialFormStepData, FormStep } from "./form-step";
import { ConfirmStep } from "./confirm-step";
import { withRouter, RouteComponentProps } from "react-router-dom";
import updateServicesQuery from "../models/update-service-metadata.mutation.graphql";
import { Modal } from "../../../../palette";

import { bulkEditServices_search_edges_node_Service } from "../models/__generated__/bulkEditServices";
import {
  updateServices,
  updateServicesVariables
} from "../models/__generated__/updateServices";
import {
  MultiStepWizard,
  CustomDrawer,
  withDrawerContext,
  DrawerContextProps,
  FlagProps,
  withShowFlag,
  ChildProps,
  compose,
  MutationFunc,
  graphql
} from "@atlassiansox/microscopekit";

const { parse } = require("query-string");
const AkSpinner = require("@atlaskit/spinner").default;

type Services = bulkEditServices_search_edges_node_Service;

export interface BulkEditState {
  services: Services[];
  showConfirmModal: boolean;
  updating: boolean;
  steps: {
    form: FormStepData;
  };
}
export const initialState: BulkEditState = {
  services: [],
  showConfirmModal: false,
  updating: false,
  steps: {
    form: initialFormStepData
  }
};

type BulkEditProps = RouteComponentProps<any> &
  FlagProps &
  DrawerContextProps &
  ChildProps<
    {
      updateServices: MutationFunc<updateServices, updateServicesVariables>;
    },
    {}
  >;

export class BulkEditView extends React.Component<
  BulkEditProps,
  BulkEditState
> {
  constructor(props: BulkEditProps) {
    super(props);
    this.state = initialState;
  }

  render() {
    const { query } = parse(this.props.location.search) as any;

    return (
      <CustomDrawer
        id="bulk-edit-drawer"
        onCloseDrawer={() => {
          this.setState(initialState);
        }}
      >
        <Layout className="bulk-edit">
          {this.state.showConfirmModal ? this.renderConfirmModal() : null}
          <MultiStepWizard
            steps={[
              {
                body: (
                  <SelectStep
                    defaultServices={this.state.services}
                    query={query}
                    setServices={(services: Services[]) => {
                      this.setState({ services });
                    }}
                  />
                ),
                name: "Choose Services",
                nextDisabled:
                  this.state.services.length === 0 ? "disable" : undefined
              },
              {
                body: (
                  <FormStep
                    query={query}
                    servicesCount={this.state.services.length}
                    data={this.state.steps.form}
                    setStepData={data => {
                      this.setState({
                        steps: {
                          ...this.state.steps,
                          form: { ...this.state.steps.form, ...data }
                        }
                      });
                    }}
                  />
                ),
                name: "Change Attributes",
                nextDisabled:
                  Object.keys(this.state.steps.form.values).length <= 0 ||
                  Object.keys(this.state.steps.form.errors).length > 0
                    ? "disable"
                    : undefined
              },
              {
                body: (
                  <ConfirmStep
                    services={this.state.services}
                    values={this.state.steps.form.values}
                  />
                ),
                name: "Confirm",
                onNext: () => {
                  this.setState({ showConfirmModal: true });
                }
              }
            ]}
          />
        </Layout>
      </CustomDrawer>
    );
  }

  renderConfirmModal = () => {
    const hideConfirmation = () => this.setState({ showConfirmModal: false });
    return (
      <Modal
        heading="Confirmation"
        actions={[
          {
            text: "Update",
            onClick: this.applyChanges,
            isDisabled: this.state.updating,
            iconBefore: this.state.updating ? (
              <AkSpinner size="small" isCompleting={false} />
            ) : (
              undefined
            )
          },
          {
            text: "Cancel",
            onClick: hideConfirmation,
            isDisabled: this.state.updating
          }
        ]}
        onClose={hideConfirmation}
      >
        Are you sure?
      </Modal>
    );
  };

  applyChanges = async () => {
    const selectedIds = this.state.services.map(service => service.id);
    const { values } = this.state.steps.form;
    this.setState({ updating: true });

    track("bulk updating services", {
      category: "info",
      element: "bulk update"
    });
    const finish = (err?: any) => {
      this.setState({ updating: false });
      if (!err) {
        this.props.showFlag({
          type: "success",
          title: "Success",
          message: "Services successfully updated."
        });
        this.props.updateOpenDrawer(null);
        this.setState(initialState);
      } else {
        this.props.showFlag({
          type: "error",
          title: "Error",
          message: err.toString()
        });
      }
    };
    try {
      await Promise.all(
        selectedIds.map(serviceId =>
          this.props.updateServices({
            variables: {
              input: { id: serviceId, metadata: values }
            }
          })
        )
      );

      track("bulk update services successfully finished", {
        category: "info",
        label: "bulk update"
      });
      finish();
    } catch (err) {
      track("bulk update services failed", {
        category: "error",
        label: "bulk update"
      });
      logger.error(err);
      finish(err);
    }
  };
}

export const BulkEdit = compose(
  graphql(updateServicesQuery, {
    name: "updateServices"
  }),
  withShowFlag,
  withRouter,
  withDrawerContext
)(BulkEditView);
